import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { mfaApi, MfaEnrolment } from '../../api/compliance';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/Badge';

/**
 * Admin multi-factor enrolment.
 *
 * ## Why this page exists before any of the compliance screens
 *
 * `MfaRequiredGuard` refuses every `compliance.*`, `approvals.*` and
 * `payroll.manage` action from an admin without a second factor. Without a
 * way to enrol, the person the compliance console was built for cannot open
 * it — the control would lock out exactly the user it protects.
 *
 * ## Recovery codes are shown once
 *
 * They are stored hashed, so there is no endpoint that can return them again.
 * The page says so plainly and makes the reader confirm before it clears them
 * from the screen; a quiet "save these" that scrolls away is how people end up
 * locked out of an account that can freeze customers.
 */
export const MfaSetup: React.FC = () => {
  usePage('Two-Factor Authentication');
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [enrolment, setEnrolment] = useState<MfaEnrolment | null>(null);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = await mfaApi.status();
      setEnrolled(status.enrolled);
    } catch {
      toast.error('Could not load your two-factor status.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function begin() {
    setBusy(true);
    try {
      setEnrolment(await mfaApi.enrol());
    } catch {
      toast.error('Could not start enrolment.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (token.trim().length !== 6) return;
    setBusy(true);
    try {
      const result = await mfaApi.confirm(token.trim());
      // Held in state deliberately — this is the only time they exist in
      // plaintext anywhere.
      setRecoveryCodes(result.recoveryCodes);
      setEnrolled(true);
      setEnrolment(null);
      setToken('');
    } catch {
      toast.error('That code was not accepted. Check your authenticator app.');
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!resetToken.trim()) return;
    setBusy(true);
    try {
      await mfaApi.reset(resetToken.trim());
      setEnrolled(false);
      setResetToken('');
      toast.success('Two-factor authentication removed.');
    } catch {
      toast.error('A valid code or recovery code is required to reset.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;

  // Shown once, and only once. Nothing can retrieve these again.
  if (recoveryCodes) {
    return (
      <Card>
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Save your recovery codes</h2>
          <p className="text-sm text-red-600 font-medium">
            These are shown once and cannot be retrieved later. Store them
            somewhere safe and offline. Without them, losing your authenticator
            means losing access to compliance tooling.
          </p>
          <div className="grid grid-cols-2 gap-2 rounded bg-gray-50 p-4 font-mono text-sm">
            {recoveryCodes.map((code) => (
              <div key={code}>{code}</div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            I have saved these codes somewhere I can reach without this account.
          </label>
          <Button
            disabled={!acknowledged}
            onClick={() => {
              setRecoveryCodes(null);
              setAcknowledged(false);
            }}
          >
            Done
          </Button>
        </div>
      </Card>
    );
  }

  if (enrolled) {
    return (
      <Card>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Two-factor authentication</h2>
            <Badge variant="success">Enabled</Badge>
          </div>
          <p className="text-sm text-gray-600">
            Compliance, approvals and payroll actions require a code from your
            authenticator app.
          </p>
          <div className="border-t pt-4 space-y-2">
            <h3 className="text-sm font-semibold">Move to a new device</h3>
            <p className="text-sm text-gray-600">
              Enter a current code or one of your recovery codes to remove the
              existing authenticator, then enrol again.
            </p>
            <div className="flex gap-2">
              <input
                className="rounded border px-3 py-2 text-sm"
                placeholder="Code or recovery code"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
              />
              <Button variant="danger" disabled={busy || !resetToken.trim()} onClick={reset}>
                Remove
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Set up two-factor authentication</h2>
        <p className="text-sm text-gray-600">
          Required for compliance, approvals and payroll actions. An admin
          session can freeze customer accounts and dispose of compliance
          evidence, so a password alone is not enough.
        </p>

        {!enrolment ? (
          <Button disabled={busy} onClick={begin}>
            {busy ? 'Starting…' : 'Begin setup'}
          </Button>
        ) : (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">1. Add to your authenticator</h3>
              <p className="text-sm text-gray-600">
                Scan this link, or enter the key manually.
              </p>
              <div className="mt-2 break-all rounded bg-gray-50 p-3 font-mono text-xs">
                {enrolment.uri}
              </div>
              <div className="mt-2 text-sm">
                Setup key: <span className="font-mono">{enrolment.secret}</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold">2. Confirm it works</h3>
              <p className="text-sm text-gray-600">
                Two-factor is not switched on until you enter a valid code — so
                a failed scan cannot lock you out.
              </p>
              <div className="mt-2 flex gap-2">
                <input
                  className="rounded border px-3 py-2 font-mono text-sm"
                  placeholder="000000"
                  inputMode="numeric"
                  maxLength={6}
                  value={token}
                  onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                />
                <Button disabled={busy || token.length !== 6} onClick={confirm}>
                  {busy ? 'Checking…' : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
